// [S-320c] Shared classification catalog UI — the tabbed axis browser +
// add / edit / deprecate dialogs, used by both the platform screen
// (Classification.tsx, scope="platform") and the organization screen
// (TenantClassification.tsx, scope="tenant"). The two differ only in the
// route family the data hooks hit (the `scope` prop) and whether editing
// is allowed (`readOnly` — an organization inheriting the platform catalog renders
// the entries read-only until it re-seeds).
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  LoadingState,
  Pagination,
  SectionHead,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  usePagination,
} from "../components";
import {
  useClassificationDependents,
  useClassificationList,
  useCreateClassificationEntry,
  useDeprecateClassificationEntry,
  useUpdateClassificationEntry,
  type CatalogScope,
  type ClassificationAxis,
  type ClassificationEntryWire,
  type ClassificationFacetAxis,
  type CreateClassificationInput,
} from "../data/classificationQuery";
import { getApiErrorMessage } from "../data/api";

type FacetSubTab = ClassificationFacetAxis;
const FACET_TABS: ReadonlyArray<{ value: FacetSubTab; label: string }> = [
  { value: "medium", label: "Medium" },
  { value: "guild", label: "Guild" },
  { value: "document_form", label: "Doc form" },
  { value: "budget_tier", label: "Budget tier" },
  { value: "talent_tier", label: "Talent tier" },
];

export function ClassificationCatalog({
  scope,
  readOnly = false,
}: {
  scope: CatalogScope;
  /** When true, entries render read-only (no add / edit / deprecate). */
  readOnly?: boolean;
}): JSX.Element {
  const [tab, setTab] = useState("category");
  const [facetSub, setFacetSub] = useState<FacetSubTab>("medium");
  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList ariaLabel="Classification axes" className="cls__tabs">
        <TabsTrigger value="category">Categories</TabsTrigger>
        <TabsTrigger value="contract_type">Contract types</TabsTrigger>
        <TabsTrigger value="cba">CBAs</TabsTrigger>
        <TabsTrigger value="facet">Facets</TabsTrigger>
        <TabsTrigger value="legacy_alias">Former names</TabsTrigger>
      </TabsList>

      <TabsContent value="category">
        <AxisPanel axis="category" scope={scope} readOnly={readOnly} />
      </TabsContent>
      <TabsContent value="contract_type">
        <AxisPanel axis="contract_type" scope={scope} readOnly={readOnly} />
      </TabsContent>
      <TabsContent value="cba">
        <AxisPanel axis="cba" scope={scope} readOnly={readOnly} />
      </TabsContent>
      <TabsContent value="facet">
        <div className="cls__subtabs" data-testid="cls-facet-subtabs">
          {FACET_TABS.map((t) => (
            <Button
              key={t.value}
              variant="bare"
              className={
                facetSub === t.value ? "cls__subtab is-on" : "cls__subtab"
              }
              onClick={() => setFacetSub(t.value)}
            >
              {t.label}
            </Button>
          ))}
        </div>
        <AxisPanel
          axis="facet"
          facetAxis={facetSub}
          scope={scope}
          readOnly={readOnly}
        />
      </TabsContent>
      <TabsContent value="legacy_alias">
        <AxisPanel axis="legacy_alias" scope={scope} readOnly={readOnly} />
      </TabsContent>
    </Tabs>
  );
}

function AxisPanel({
  axis,
  facetAxis,
  scope,
  readOnly,
}: {
  axis: ClassificationAxis;
  facetAxis?: ClassificationFacetAxis;
  scope: CatalogScope;
  readOnly: boolean;
}): JSX.Element {
  const { data, isLoading, isError, error, isFetching } = useClassificationList(
    scope,
    axis,
    facetAxis
  );
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ClassificationEntryWire | null>(null);
  const [deprecating, setDeprecating] =
    useState<ClassificationEntryWire | null>(null);
  // Only the rendered rows are paginated — dialogs below (successor picker,
  // dependents) still read the full `data.entries` set.
  const pager = usePagination(data?.entries ?? [], 25);

  return (
    <>
      <SectionHead
        title={axisTitle(axis, facetAxis)}
        action={
          <>
            <span className="cls__panel__head__sub">
              {data?.entries.length ?? 0} active
            </span>
            {!readOnly && (
              <Button
                variant="primary"
                className="btn--s"
                onClick={() => setAdding(true)}
                data-testid={`cls-add-${axis}`}
              >
                + Add entry
              </Button>
            )}
          </>
        }
      />

      {(isLoading ||
        (isFetching && (data?.entries.length ?? 0) === 0)) && (
        <LoadingState label="Loading classifications…" />
      )}
      {isError && (
        <Alert label="Failed to load" tone="error">
          {error?.message ?? "unknown error"}
        </Alert>
      )}
      {data !== undefined && !isFetching && data.entries.length === 0 && (
        <div className="cls__empty">No active entries yet.</div>
      )}
      {data !== undefined && data.entries.length > 0 && (
        <table className="cls__tbl" data-testid={`cls-table-${axis}`}>
          <thead>
            <tr>
              <th>Label</th>
              <th>ID</th>
              {axis === "contract_type" && (
                <>
                  <th>Category</th>
                  <th>Short form</th>
                  <th>Aliases</th>
                </>
              )}
              {axis === "cba" && (
                <>
                  <th>Guild</th>
                  <th>Effective from</th>
                </>
              )}
              {axis === "legacy_alias" && <th>Resolves to</th>}
              <th>Status</th>
              {!readOnly && <th style={{ width: 120 }} />}
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((e) => (
              <tr key={`${e.axis}.${e.id}`} data-testid={`cls-row-${e.id}`}>
                <td className="nm">{e.label}</td>
                <td>
                  <span className="id">{e.id}</span>
                </td>
                {axis === "contract_type" && (
                  <>
                    <td>
                      <span className="chip">{e.categoryId ?? "—"}</span>
                    </td>
                    <td>{e.shortForm === true ? "yes" : "no"}</td>
                    <td>
                      <div className="chips">
                        {e.aliases.length === 0
                          ? "—"
                          : e.aliases.map((a) => (
                              <span className="chip" key={a}>
                                {a}
                              </span>
                            ))}
                      </div>
                    </td>
                  </>
                )}
                {axis === "cba" && (
                  <>
                    <td>
                      <span className="chip">{e.guildId ?? "—"}</span>
                    </td>
                    <td>
                      {e.effectiveFrom === null
                        ? "—"
                        : formatDate(e.effectiveFrom)}
                    </td>
                  </>
                )}
                {axis === "legacy_alias" && (
                  <td>
                    <span className="chip">{e.aliasFor ?? "—"}</span>
                  </td>
                )}
                <td>
                  <span
                    className={
                      e.effectiveTo === null
                        ? "pill cls__pill--active"
                        : "pill cls__pill--deprecated"
                    }
                  >
                    {e.effectiveTo === null ? "active" : "deprecated"}
                  </span>
                </td>
                {!readOnly && (
                  <td className="cls__row__actions">
                    {e.effectiveTo === null && (
                      <>
                        <Button
                          variant="ghost"
                          className="btn--s"
                          onClick={() => setEditing(e)}
                          data-testid={`cls-edit-${e.id}`}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          className="btn--s"
                          onClick={() => setDeprecating(e)}
                          data-testid={`cls-deprecate-${e.id}`}
                        >
                          Deprecate
                        </Button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data !== undefined && data.entries.length > 0 && (
        <Pagination
          page={pager.page}
          pageCount={pager.pageCount}
          onPageChange={pager.setPage}
          total={pager.total}
          rangeStart={pager.rangeStart}
          rangeEnd={pager.rangeEnd}
          itemLabel="entry"
        />
      )}

      {!readOnly && (
        <>
          <AddEntryDialog
            open={adding}
            axis={axis}
            facetAxis={facetAxis ?? null}
            scope={scope}
            onClose={() => setAdding(false)}
          />
          <EditEntryDialog
            entry={editing}
            facetAxis={facetAxis ?? undefined}
            scope={scope}
            onClose={() => setEditing(null)}
          />
          <DeprecateConfirmDialog
            entry={deprecating}
            facetAxis={facetAxis ?? undefined}
            scope={scope}
            siblingsActive={data?.entries ?? []}
            onClose={() => setDeprecating(null)}
          />
        </>
      )}
    </>
  );
}

function EditEntryDialog({
  entry,
  facetAxis,
  scope,
  onClose,
}: {
  entry: ClassificationEntryWire | null;
  facetAxis?: ClassificationFacetAxis;
  scope: CatalogScope;
  onClose: () => void;
}): JSX.Element | null {
  const update = useUpdateClassificationEntry(scope);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [aliasesText, setAliasesText] = useState("");
  const [shortForm, setShortForm] = useState(false);

  useEffect(() => {
    if (entry === null) return;
    setLabel(entry.label);
    setDescription(entry.description ?? "");
    setAliasesText(entry.aliases.join(", "));
    setShortForm(entry.shortForm === true);
    update.reset();
  }, [entry]);

  if (entry === null) return null;

  const close = (): void => {
    update.reset();
    onClose();
  };

  return (
    <Dialog
      open={entry !== null}
      onOpenChange={(o) => {
        if (!o) close();
      }}
      title={`Edit ${entry.label}`}
      description="Cosmetic fields only. To change the ID, category, or other structural fields, deprecate this entry and create a new one."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const patch: import("../data/classificationQuery").UpdateClassificationInput["patch"] =
            {};
          const trimmedLabel = label.trim();
          if (trimmedLabel.length > 0 && trimmedLabel !== entry.label) {
            patch.label = trimmedLabel;
          }
          const normDesc = description.trim().length === 0 ? null : description.trim();
          if (normDesc !== (entry.description ?? null)) {
            patch.description = normDesc;
          }
          if (entry.axis === "contract_type") {
            const aliases = aliasesText
              .split(",")
              .map((a) => a.trim())
              .filter((a) => a.length > 0);
            const same =
              aliases.length === entry.aliases.length &&
              aliases.every((a, i) => entry.aliases[i] === a);
            if (!same) patch.aliases = aliases;
            if (shortForm !== (entry.shortForm === true)) {
              patch.shortForm = shortForm;
            }
          }
          if (Object.keys(patch).length === 0) {
            close();
            return;
          }
          update.mutate(
            { axis: entry.axis, id: entry.id, patch, facetAxis },
            { onSuccess: () => close() }
          );
        }}
      >
        <div className="cls__modal__row">
          <label>Display label</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            data-testid="cls-edit-label"
          />
        </div>
        <div className="cls__modal__row">
          <label>Description (LLM hint)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="cls-edit-description"
          />
        </div>
        {entry.axis === "contract_type" && (
          <>
            <div className="cls__modal__row">
              <label>Aliases (comma-separated)</label>
              <input
                value={aliasesText}
                onChange={(e) => setAliasesText(e.target.value)}
                data-testid="cls-edit-aliases"
              />
            </div>
            <div className="cls__modal__row">
              <Checkbox
                checked={shortForm}
                onCheckedChange={(v) => setShortForm(v)}
                label="Short-form contract (enables workflow shortcuts)"
              />
            </div>
          </>
        )}
        {update.isError && (
          <div className="cls__error" role="alert">
            Could not save: {getApiErrorMessage(update.error) ?? update.error?.message ?? "unknown error"}
          </div>
        )}
        <div className="cls__modal__ft">
          <Button
            variant="ghost"
            type="button"
            onClick={close}
            disabled={update.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={update.isPending}
            data-testid="cls-edit-submit"
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DeprecateConfirmDialog({
  entry,
  facetAxis,
  scope,
  siblingsActive,
  onClose,
}: {
  entry: ClassificationEntryWire | null;
  facetAxis?: ClassificationFacetAxis;
  scope: CatalogScope;
  /** Other active entries on the same axis — used to populate the
   *  successor dropdown so the user picks an existing live entry
   *  rather than typing a free-text id. */
  siblingsActive: ReadonlyArray<ClassificationEntryWire>;
  onClose: () => void;
}): JSX.Element | null {
  const deprecate = useDeprecateClassificationEntry(scope);
  const dependents = useClassificationDependents(
    scope,
    entry?.axis ?? "category",
    entry?.id ?? "",
    entry !== null
  );
  const [successorId, setSuccessorId] = useState<string>("");
  const [cascade, setCascade] = useState(false);

  useEffect(() => {
    if (entry === null) return;
    setSuccessorId("");
    setCascade(false);
    deprecate.reset();
  }, [entry?.id]);

  if (entry === null) return null;

  const deps = dependents.data?.dependents ?? [];
  const hasDeps = deps.length > 0;
  const needsResolution = hasDeps && successorId.length === 0 && !cascade;
  const close = (): void => {
    deprecate.reset();
    onClose();
  };

  return (
    <Dialog
      open={entry !== null}
      onOpenChange={(o) => {
        if (!o) close();
      }}
      title={`Deprecate ${entry.label}`}
      description="Deprecated entries are kept for audit and historical references; they no longer appear in active lists or extraction prompts."
    >
      {dependents.isLoading && (
        <div className="cls__loading">Looking up dependents…</div>
      )}
      {hasDeps && (
        <div className="cls__modal__row" data-testid="cls-deprecate-deps">
          <label>
            {deps.length} active dependent{deps.length === 1 ? "" : "s"} reference
            this entry
          </label>
          <ul className="cls__deps">
            {deps.slice(0, 10).map((d) => (
              <li key={`${d.axis}.${d.id}.${d.relation}`}>
                <span className="chip">
                  {d.axis}/{d.id}
                </span>{" "}
                <span className="cls__deps__rel">via {d.relation}</span>
              </li>
            ))}
            {deps.length > 10 && (
              <li className="cls__deps__rel">…and {deps.length - 10} more</li>
            )}
          </ul>
        </div>
      )}
      {hasDeps && (
        <div className="cls__modal__row">
          <label>Successor (recommended)</label>
          <Select<string>
            label="Successor"
            value={successorId.length > 0 ? successorId : undefined}
            onValueChange={(v) => {
              const next = v ?? "";
              setSuccessorId(next);
              if (next.length > 0) setCascade(false);
            }}
            clearable
            placeholder="— none, see cascade below —"
            options={siblingsActive
              .filter((s) => s.id !== entry.id && s.effectiveTo === null)
              .map((s) => ({ value: s.id, label: `${s.label} (${s.id})` }))}
          />
          <div className="cls__modal__hint">
            Dependents continue to resolve to the successor via the chain-follow
            in <code>resolve()</code>.
          </div>
        </div>
      )}
      {hasDeps && (
        <div className="cls__modal__row">
          <Checkbox
            checked={cascade}
            onCheckedChange={(v) => {
              setCascade(v);
              if (v) setSuccessorId("");
            }}
            label="Cascade — also deprecate every dependent entry above"
          />
          <div className="cls__modal__hint">
            Use cascade when the whole subtree is being retired. Each dependent
            gets its own audit event tagged with this entry's id as the origin.
          </div>
        </div>
      )}
      {!hasDeps && dependents.isFetched && (
        <div className="cls__modal__row">
          <label data-testid="cls-deprecate-no-deps">
            No active entries reference this — safe to deprecate.
          </label>
        </div>
      )}
      {deprecate.isError && (
        <div className="cls__error" role="alert">
          Could not deprecate: {deprecate.error?.message ?? "unknown error"}
        </div>
      )}
      <div className="cls__modal__ft">
        <Button
          variant="ghost"
          type="button"
          onClick={close}
          disabled={deprecate.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          disabled={
            deprecate.isPending || dependents.isLoading || needsResolution
          }
          data-testid="cls-deprecate-confirm"
          onClick={() => {
            deprecate.mutate(
              {
                axis: entry.axis,
                facetAxis,
                id: entry.id,
                asOf: new Date().toISOString(),
                successorId: successorId.length > 0 ? successorId : null,
                ...(cascade ? { cascade: true } : {}),
              },
              { onSuccess: () => close() }
            );
          }}
        >
          {deprecate.isPending
            ? "Deprecating…"
            : cascade
              ? "Cascade deprecate"
              : "Deprecate"}
        </Button>
      </div>
    </Dialog>
  );
}

function AddEntryDialog({
  open,
  axis,
  facetAxis,
  scope,
  onClose,
}: {
  open: boolean;
  axis: ClassificationAxis;
  facetAxis: ClassificationFacetAxis | null;
  scope: CatalogScope;
  onClose: () => void;
}): JSX.Element | null {
  const create = useCreateClassificationEntry(scope);
  // [bugfix] The category picker's options — the SAME catalog the FK resolves
  // against, so the form can only offer values the server will accept.
  const categories = useClassificationList(scope, "category");
  // [taxonomy integrity] The other two FK targets, read from the same catalog
  // the server validates against: cba.guildId -> facet.guild, and
  // legacy_alias.aliasFor -> an active contract type.
  const guildFacets = useClassificationList(scope, "facet", "guild");
  const guildOpts = (guildFacets.data?.entries ?? [])
    .filter((g) => g.effectiveTo === null)
    .map((g) => ({ value: g.id, label: g.label + " (" + g.id + ")" }));
  const contractTypes = useClassificationList(scope, "contract_type");
  const contractTypeOpts = (contractTypes.data?.entries ?? [])
    .filter((c) => c.effectiveTo === null)
    .map((c) => ({ value: c.id, label: c.label + " (" + c.id + ")" }));
  const categoryOpts = (categories.data?.entries ?? [])
    .filter((c) => c.effectiveTo === null)
    .map((c) => ({ value: c.id, label: `${c.label} (${c.id})` }));
  // [bugfix follow-up] Inline category creation. A SECOND mutation instance on
  // purpose: sharing the contract-type one would mix this pending/error state
  // into the main form.
  const createCategory = useCreateClassificationEntry(scope);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState("");
  const [newCategoryId, setNewCategoryId] = useState("");
  const [id, setId] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [shortForm, setShortForm] = useState(false);
  const [aliasesText, setAliasesText] = useState("");
  const [guildId, setGuildId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [aliasFor, setAliasFor] = useState("");

  // Keep the id in step with the label until the user edits the id directly —
  // typing a label and getting a valid kebab id for free is the whole point.
  const slug = (v: string): string =>
    v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const onCreateCategory = async () => {
    const label = newCategoryLabel.trim();
    const id = (newCategoryId.trim().length > 0 ? newCategoryId : slug(label)).trim();
    if (label.length === 0 || id.length === 0) return;
    // Same endpoint, same FK universe — this is a real category entry, not a
    // free-text escape hatch, so the hierarchy stays intact.
    await createCategory.mutateAsync({ axis: "category", id, label, description: null });
    setCategoryId(id);
    setAddingCategory(false);
    setNewCategoryLabel("");
    setNewCategoryId("");
    await categories.refetch();
  };

  if (!open) return null;

  const reset = (): void => {
    setId("");
    setLabel("");
    setDescription("");
    setCategoryId("");
    setShortForm(false);
    setAliasesText("");
    setGuildId("");
    setEffectiveFrom("");
    setAliasFor("");
    create.reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
      title={`Add ${humanAxis(axis, facetAxis)} entry`}
      description="New entries become active immediately; Stage 2 LLM extraction picks them up on the next ingestion run."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const input: CreateClassificationInput = {
            axis,
            id: id.trim(),
            label: label.trim(),
            description: description.trim().length === 0 ? null : description.trim(),
          };
          if (axis === "contract_type") {
            input.categoryId = categoryId.trim().length === 0 ? null : categoryId.trim();
            input.shortForm = shortForm;
            input.aliases = aliasesText
              .split(",")
              .map((a) => a.trim())
              .filter((a) => a.length > 0);
          }
          if (axis === "cba") {
            input.guildId = guildId.trim().length === 0 ? null : guildId.trim();
            input.effectiveFrom =
              effectiveFrom.length === 0
                ? null
                : new Date(effectiveFrom).toISOString();
          }
          if (axis === "facet") {
            input.facetAxis = facetAxis;
          }
          if (axis === "legacy_alias") {
            input.aliasFor = aliasFor.trim().length === 0 ? null : aliasFor.trim();
          }
          create.mutate(input, {
            onSuccess: () => {
              reset();
              onClose();
            },
          });
        }}
      >
        <div className="cls__modal__row">
          <label>ID</label>
          <input
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder={hintFor(axis, facetAxis)}
            required
            data-testid="cls-modal-id"
          />
          <div className="cls__modal__hint">
            Kebab-case. This is what audit events + Stage 2 LLM output use.
          </div>
        </div>
        <div className="cls__modal__row">
          <label>Display label</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            data-testid="cls-modal-label"
          />
        </div>
        <div className="cls__modal__row">
          <label>Description (LLM hint)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="One-line description; embedded in the Stage 2 prompt."
          />
        </div>

        {axis === "contract_type" && (
          <>
            <div className="cls__modal__row">
              <span className="cls__modal__lblrow">
                <label>Category</label>
                {/* [bugfix follow-up] The picker closed the typo hole but
                    would otherwise be a dead end when the category genuinely
                    does not exist yet. This creates a real category entry
                    through the same API and selects it — no free text, and the
                    contract_type -> categoryId FK still resolves. */}
                <Button
                  variant="ghost"
                  className="btn--xs"
                  onClick={() => setAddingCategory((v) => !v)}
                  data-testid="cls-add-category"
                >
                  {addingCategory ? "Cancel" : "+ New category"}
                </Button>
              </span>
              {/* [bugfix] Was a free-text input whose placeholder listed the
                  valid ids slash-separated — which reads as a value pattern,
                  not a menu. Observed live: a reviewer typed
                  "talent / actor / cast" and the save 422d, because categoryId
                  is a FOREIGN KEY that has to match one of the ten category
                  entries exactly. A picker cannot be typed wrong. */}
              <Select
                label="Category"
                value={categoryId.length === 0 ? undefined : categoryId}
                options={categoryOpts}
                onValueChange={(v) => setCategoryId(v ?? "")}
                // [nesting] While the sub-form is open it OWNS this value —
                // it ends by selecting what it created. Leaving the picker live
                // would give one field two writers, and the later one wins
                // silently.
                disabled={addingCategory}
                triggerClassName="cls__modal__select"
              />
            </div>
            {addingCategory && (
              <div className="cls__modal__row cls__modal__subform">
                <label>New category</label>
                <input
                  value={newCategoryLabel}
                  onChange={(e) => setNewCategoryLabel(e.target.value)}
                  placeholder="Display label, e.g. Talent"
                  data-testid="cls-new-category-label"
                />
                <input
                  value={newCategoryId}
                  onChange={(e) => setNewCategoryId(e.target.value)}
                  placeholder="id (kebab-case)"
                  data-testid="cls-new-category-id"
                />
                <Button
                  variant="secondary"
                  className="btn--xs"
                  disabled={newCategoryLabel.trim().length === 0 || createCategory.isPending}
                  loading={createCategory.isPending}
                  onClick={() => void onCreateCategory()}
                  data-testid="cls-new-category-save"
                >
                  Create + select
                </Button>
                {createCategory.isError && (
                  <span className="cls__modal__err">
                    {getApiErrorMessage(createCategory.error) ??
                      createCategory.error?.message ??
                      "Could not create the category."}
                  </span>
                )}
              </div>
            )}
            <div className="cls__modal__row">
              <Checkbox
                checked={shortForm}
                onCheckedChange={(v) => setShortForm(v)}
                label="Short-form contract (enables workflow shortcuts)"
              />
            </div>
            <div className="cls__modal__row">
              <label>Aliases (comma-separated)</label>
              <input
                value={aliasesText}
                onChange={(e) => setAliasesText(e.target.value)}
                placeholder="actor_deal_memo, performer-agreement"
              />
            </div>
          </>
        )}

        {axis === "cba" && (
          <>
            <div className="cls__modal__row">
              <label>Guild</label>
              {/* [taxonomy integrity] Same FK, same fix as categoryId: the
                  server resolves this against facet entries whose facetAxis is
                  "guild" and rejects anything else. It was a free-text input
                  whose placeholder read as a value pattern. */}
              <Select
                label="Guild"
                value={guildId.length === 0 ? undefined : guildId}
                options={guildOpts}
                onValueChange={(v) => setGuildId(v ?? "")}
                triggerClassName="cls__modal__select"
              />
            </div>
            <div className="cls__modal__row">
              <label>Effective from</label>
              <input
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                required
              />
            </div>
          </>
        )}

        {axis === "legacy_alias" && (
          <div className="cls__modal__row">
            <label>Resolves to (contract type)</label>
            {/* [taxonomy integrity] aliasFor must resolve to an ACTIVE entry
                (validateReferences -> findAnywhere). Picking from the live
                contract types is the only way to guarantee that; a typo here
                used to surface as a bare 422. */}
            <Select
              label="Resolves to"
              value={aliasFor.length === 0 ? undefined : aliasFor}
              options={contractTypeOpts}
              onValueChange={(v) => setAliasFor(v ?? "")}
              triggerClassName="cls__modal__select"
            />
          </div>
        )}

        {create.isError && (
          <div className="cls__error" role="alert">
            Could not save: {getApiErrorMessage(create.error) ?? create.error?.message ?? "unknown error"}
          </div>
        )}

        <div className="cls__modal__ft">
          <Button
            variant="ghost"
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            // [nesting] Submitting mid-sub-form would save the OUTER entry
            // with whatever the picker held before the user started creating a
            // replacement — a silent wrong parent. Finish or cancel first.
            disabled={
              create.isPending || id.length === 0 || label.length === 0 || addingCategory
            }
            data-testid="cls-modal-submit"
          >
            {create.isPending ? "Saving…" : "Add entry"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

function axisTitle(
  axis: ClassificationAxis,
  facetAxis?: ClassificationFacetAxis
): string {
  if (axis === "facet" && facetAxis !== undefined) {
    return facetTitle(facetAxis);
  }
  switch (axis) {
    case "category":
      return "Categories";
    case "contract_type":
      return "Contract types";
    case "cba":
      return "Anchor CBAs";
    case "facet":
      return "Facets";
    case "legacy_alias":
      return "Former names";
  }
}

function facetTitle(f: ClassificationFacetAxis): string {
  switch (f) {
    case "medium":
      return "Medium";
    case "guild":
      return "Guild / union";
    case "document_form":
      return "Document form";
    case "budget_tier":
      return "Budget tier";
    case "talent_tier":
      return "Talent tier";
  }
}

function humanAxis(
  axis: ClassificationAxis,
  facetAxis: ClassificationFacetAxis | null
): string {
  if (axis === "facet" && facetAxis !== null) {
    return facetTitle(facetAxis).toLowerCase();
  }
  return axisTitle(axis).toLowerCase();
}

function hintFor(
  axis: ClassificationAxis,
  facetAxis: ClassificationFacetAxis | null
): string {
  switch (axis) {
    case "category":
      return "talent";
    case "contract_type":
      return "actor-agreement";
    case "cba":
      return "sag-aftra-2026-theatrical";
    case "facet":
      if (facetAxis === "medium") return "medium.streaming";
      if (facetAxis === "guild") return "csa";
      return "facet.<id>";
    case "legacy_alias":
      return "actor_deal_memo";
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
